import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Charts here are plain elements rather than a charting library: the forms the
 * SRS needs (magnitude by category, two measures per member, spend over time)
 * are all bar-shaped, and hand-built marks keep the 4px rounded data-ends, the
 * 2px inter-bar gap and the theme's own colours exact.
 *
 * Every mark is directly labelled, so colour is never the only channel carrying
 * meaning; the tooltip adds detail rather than being the only way to read it.
 */

type Tip = { x: number; y: number; content: ReactNode } | null;

function Tooltip({ tip }: { tip: Tip }) {
  if (!tip) return null;

  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-[calc(100%+10px)] whitespace-nowrap rounded-lg border border-line bg-surface px-2.5 py-1.5 text-[12px] shadow-lg"
      style={{ left: tip.x, top: tip.y }}
    >
      {tip.content}
    </div>
  );
}

export type BarDatum = {
  key: string;
  label: string;
  /** Displayed at the end of the bar. */
  value: string;
  /** 0–1, relative to the largest row (not to the total). */
  fraction: number;
  color: string;
  caption?: string;
  tooltip?: ReactNode;
  onClick?: () => void;
};

/** Horizontal bars — magnitude compared across a named set. */
export function BarList({ data }: { data: BarDatum[] }) {
  const [tip, setTip] = useState<Tip>(null);

  return (
    <div className="flex flex-col gap-3.5">
      {data.map((row) => (
        <div
          key={row.key}
          className={cn('group', row.onClick && 'cursor-pointer')}
          onClick={row.onClick}
          onMouseMove={(event) =>
            row.tooltip &&
            setTip({ x: event.clientX, y: event.clientY, content: row.tooltip })
          }
          onMouseLeave={() => setTip(null)}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 text-[13px]">
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: row.color }}
                aria-hidden
              />
              <span className="truncate">{row.label}</span>
            </span>
            <span className="tnum shrink-0 text-[13px] font-semibold">{row.value}</span>
          </div>

          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-[4px] bg-elevated">
            <div
              className="h-full rounded-[4px] transition-[width] duration-300"
              style={{
                width: `${Math.max(1.5, Math.min(100, row.fraction * 100))}%`,
                backgroundColor: row.color,
              }}
            />
          </div>

          {row.caption && <p className="mt-1 text-[12px] text-muted">{row.caption}</p>}
        </div>
      ))}

      <Tooltip tip={tip} />
    </div>
  );
}

export type GroupedDatum = {
  key: string;
  label: string;
  series: { name: string; value: number; display: string; color: string }[];
  caption?: string;
  onClick?: () => void;
};

/**
 * Two measures per row, as paired bars on one shared scale — never a second
 * axis. `max` is the scale maximum across every row and series.
 */
export function GroupedBars({ data, max }: { data: GroupedDatum[]; max: number }) {
  const [tip, setTip] = useState<Tip>(null);
  const scale = max > 0 ? max : 1;

  return (
    <div className="flex flex-col gap-4">
      {data.map((row) => (
        <div
          key={row.key}
          className={cn(row.onClick && 'cursor-pointer')}
          onClick={row.onClick}
          onMouseLeave={() => setTip(null)}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-[13px] font-medium">{row.label}</span>
            {row.caption && (
              <span className="shrink-0 text-[12px] text-muted">{row.caption}</span>
            )}
          </div>

          {/* 2px gap between the paired bars keeps the fills from touching. */}
          <div className="mt-1.5 flex flex-col gap-0.5">
            {row.series.map((series) => (
              <div
                key={series.name}
                className="flex items-center gap-2"
                onMouseMove={(event) =>
                  setTip({
                    x: event.clientX,
                    y: event.clientY,
                    content: (
                      <span className="flex items-center gap-1.5">
                        <span
                          className="size-2 rounded-full"
                          style={{ backgroundColor: series.color }}
                        />
                        {row.label} · {series.name} <b className="tnum">{series.display}</b>
                      </span>
                    ),
                  })
                }
              >
                <div className="h-2.5 flex-1 overflow-hidden rounded-[4px] bg-elevated">
                  <div
                    className="h-full rounded-[4px] transition-[width] duration-300"
                    style={{
                      width: `${Math.max(1.5, (series.value / scale) * 100)}%`,
                      backgroundColor: series.color,
                    }}
                  />
                </div>
                <span className="tnum w-24 shrink-0 text-right text-[12px] text-muted">
                  {series.display}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Tooltip tip={tip} />
    </div>
  );
}

export type ColumnDatum = {
  key: string;
  label: string;
  value: number;
  display: string;
  caption?: string;
};

/** Discrete periods over time, left to right. Single series — no legend. */
export function ColumnChart({
  data,
  color,
  height = 180,
}: {
  data: ColumnDatum[];
  color: string;
  height?: number;
}) {
  const [tip, setTip] = useState<Tip>(null);
  const max = Math.max(...data.map((row) => row.value), 1);

  return (
    <div>
      <div
        className="relative flex items-end gap-1.5 border-b border-line"
        style={{ height }}
        onMouseLeave={() => setTip(null)}
      >
        {/* Recessive gridlines at 25% intervals, behind the marks. */}
        {[0.25, 0.5, 0.75, 1].map((step) => (
          <span
            key={step}
            aria-hidden
            className="absolute inset-x-0 border-t border-line/60"
            style={{ bottom: `${step * 100}%` }}
          />
        ))}

        {data.map((row) => (
          <div
            key={row.key}
            className="relative flex h-full min-w-0 flex-1 items-end"
            onMouseMove={(event) =>
              setTip({
                x: event.clientX,
                y: event.clientY,
                content: (
                  <span>
                    {row.label} <b className="tnum">{row.display}</b>
                    {row.caption && <span className="text-muted"> · {row.caption}</span>}
                  </span>
                ),
              })
            }
          >
            <div
              className="w-full rounded-t-[4px] transition-[height] duration-300 hover:brightness-110"
              style={{
                height: `${Math.max(2, (row.value / max) * 100)}%`,
                backgroundColor: color,
              }}
            />
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex gap-1.5">
        {data.map((row) => (
          <span
            key={row.key}
            className="min-w-0 flex-1 truncate text-center text-[11px] text-muted"
            title={row.label}
          >
            {row.label}
          </span>
        ))}
      </div>

      <Tooltip tip={tip} />
    </div>
  );
}

/** Legend — always present for two or more series. */
export function Legend({ items }: { items: { name: string; color: string }[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((item) => (
        <li key={item.name} className="flex items-center gap-1.5 text-[12px] text-muted">
          <span
            className="size-2.5 rounded-full"
            style={{ backgroundColor: item.color }}
            aria-hidden
          />
          {item.name}
        </li>
      ))}
    </ul>
  );
}
